import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { servicesData } from '../../data/mock';
import ServiceModal from './ServiceModal';
import { AnimatedBorder } from './BorderBeam';

// Brand logos as SVG components (for services without image files)
const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="w-10 h-10">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const ZoomLogo = () => (
  <svg viewBox="0 0 100 100" className="w-10 h-10">
    <rect width="100" height="100" rx="20" fill="#2D8CFF"/>
    <path d="M20 35v30c0 4 3 7 7 7h35c4 0 7-3 7-7V55l15 12V33L69 45V35c0-4-3-7-7-7H27c-4 0-7 3-7 7z" fill="white"/>
  </svg>
);

const GlobeLogo = () => (
  <svg viewBox="0 0 100 100" className="w-10 h-10">
    <circle cx="50" cy="50" r="50" fill="#10B981"/>
    <circle cx="50" cy="50" r="35" fill="none" stroke="white" strokeWidth="3"/>
    <ellipse cx="50" cy="50" rx="15" ry="35" fill="none" stroke="white" strokeWidth="3"/>
    <line x1="15" y1="50" x2="85" y2="50" stroke="white" strokeWidth="3"/>
    <line x1="50" y1="15" x2="50" y2="85" stroke="white" strokeWidth="3"/>
  </svg>
);

const WhatsAppLogo = () => (
  <svg viewBox="0 0 100 100" className="w-10 h-10">
    <circle cx="50" cy="50" r="50" fill="#25D366"/>
    <path d="M71 58.5c-1.2-.6-7.3-3.6-8.4-4-.8-.4-1.9-.6-2.8.6-.8 1.2-3.2 4-3.9 4.8-.7.8-1.4.9-2.7.3-1.2-.6-5.2-1.9-9.9-6.1-3.7-3.3-6.1-7.3-6.9-8.5-.7-1.2-.1-1.9.5-2.5.6-.5 1.2-1.4 1.8-2.2.6-.7.8-1.2 1.2-2.1.4-.8.2-1.5-.1-2.2-.3-.6-2.8-6.7-3.8-9.1-1-2.4-2-2.1-2.8-2.1-.7 0-1.5 0-2.4 0-.8 0-2.2.3-3.3 1.5-1.1 1.2-4.3 4.2-4.3 10.3 0 6.1 4.4 11.9 5 12.7.6.8 8.7 13.3 21.1 18.7 2.9 1.3 5.2 2 7 2.6 3 .9 5.6.8 7.8.5 2.4-.4 7.3-3 8.3-5.9 1-2.9 1-5.3.7-5.9-.3-.5-1.1-.8-2.3-1.4z" fill="white"/>
  </svg>
);

const logoComponents = {
  Mail: GoogleLogo,
  Video: ZoomLogo,
  Globe: GlobeLogo,
  MessageSquare: WhatsAppLogo,
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut',
    },
  }),
};

// Orbiting dot around card
const OrbitDot = ({ delay, duration, color }) => (
  <motion.div
    className="absolute w-full h-full pointer-events-none"
    animate={{ rotate: 360 }}
    transition={{ duration, repeat: Infinity, ease: 'linear', delay }}
  >
    <div 
      className={`absolute w-2 h-2 rounded-full ${color} shadow-lg`}
      style={{ top: '-4px', left: '50%', marginLeft: '-4px' }}
    />
  </motion.div>
);

const ServiceCard = ({ service, index, onClick }) => {
  const LogoComponent = logoComponents[service.icon];
  
  const cardStyles = {
    1: { bg: 'bg-gradient-to-br from-cyan-50 to-teal-50', border: 'border-cyan-200', colorFrom: '#06b6d4', colorTo: '#14b8a6', orbit: 'bg-cyan-400' },
    2: { bg: 'bg-gradient-to-br from-blue-50 to-indigo-50', border: 'border-blue-200', colorFrom: '#3b82f6', colorTo: '#6366f1', orbit: 'bg-blue-400' },
    3: { bg: 'bg-gradient-to-br from-sky-50 to-blue-50', border: 'border-sky-200', colorFrom: '#0ea5e9', colorTo: '#3b82f6', orbit: 'bg-sky-400' },
    4: { bg: 'bg-gradient-to-br from-emerald-50 to-green-50', border: 'border-emerald-200', colorFrom: '#10b981', colorTo: '#22c55e', orbit: 'bg-emerald-400' },
    5: { bg: 'bg-gradient-to-br from-red-50 to-orange-50', border: 'border-red-200', colorFrom: '#dc2626', colorTo: '#f97316', orbit: 'bg-red-400' },
    6: { bg: 'bg-gradient-to-br from-green-50 to-emerald-50', border: 'border-green-200', colorFrom: '#22c55e', colorTo: '#10b981', orbit: 'bg-green-400' },
  };

  const style = cardStyles[service.id];

  return (
    <motion.div
      custom={index}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      whileHover={{ scale: 1.02, y: -5 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onClick(service)}
      className={`relative p-6 lg:p-8 rounded-2xl border-2 transition-all duration-300 group cursor-pointer overflow-hidden ${style.bg} ${style.border}`}
    >
      {/* Animated Border Beam Effect - Always visible */}
      <AnimatedBorder colorFrom={style.colorFrom} colorTo={style.colorTo} duration={3} />
      
      {/* Orbiting dot */}
      <OrbitDot delay={index * 0.5} duration={12 + index * 2} color={style.orbit} />
      
      {/* Decorative arc */}
      <svg className="absolute -right-10 -bottom-10 w-40 h-40 opacity-10" viewBox="0 0 100 100">
        <path
          d="M 10 50 Q 50 10 90 50"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-gray-400"
        />
      </svg>

      {/* Logo */}
      <div className="relative z-10 mb-5">
        {service.logo ? (
          <img 
            src={service.logo} 
            alt={service.title}
            className="w-12 h-12 rounded-xl object-contain"
          />
        ) : (
          LogoComponent && <LogoComponent />
        )}
      </div>

      {/* Content */}
      <h3 className="relative z-10 text-xl font-semibold text-gray-900 mb-3 group-hover:text-gray-700 transition-colors">
        {service.title}
      </h3>
      <p className="relative z-10 text-gray-600 leading-relaxed text-sm lg:text-base">
        {service.description}
      </p>

      {/* Click indicator */}
      <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <span className="text-xs text-gray-400 font-medium">Klik untuk detail →</span>
      </div>
    </motion.div>
  );
};

const FeaturesGrid = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCardClick = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedService(null), 300);
  };

  return (
    <>
      <section id="layanan" className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-100/30 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-100/30 rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-medium mb-4">
              Layanan Kami
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Infrastruktur Digital
              <span className="block text-gray-400">untuk PPI Seluruh Dunia</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-lg">
              Solusi teknologi terintegrasi untuk mendukung aktivitas dan produktivitas organisasi pelajar Indonesia.
            </p>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {servicesData.map((service, index) => (
              <ServiceCard 
                key={service.id} 
                service={service} 
                index={index}
                onClick={handleCardClick}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      <ServiceModal 
        service={selectedService}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default FeaturesGrid;
